import { Box, createFilterOptions, MenuItem, useTheme } from '@mui/material';
import Autocomplete from 'components/autocomplete';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Select from 'components/select';
import { ArrowRight, Info } from 'lucide-react';
import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { FeedMapping } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-feed-map';
import { FeedProgramMap } from 'views/bluviu-nutrition/features/scenarios-v2/types/feed-program-mapping.type';
import { FeedOption } from 'views/bluviu-nutrition/features/scenarios-v2/types/feed-active-products.type';
import FormListCell from 'components/list/form-list-cell';

type FeedProgramMappingFormInput = {
  feedMappings: Record<string, FeedOption | null>;
};

type FeedProgramMappingAccordionProps = {
  feedProgram: FeedProgramMap;
  feedOptions: FeedOption[];
  isLoading: boolean;
};

const FeedProgramMapping: React.FC<FeedProgramMappingAccordionProps> = ({
  feedProgram,
  feedOptions,
  isLoading,
}) => {
  const { control } = useFormContext<FeedProgramMappingFormInput>();
  const feedMappings = useWatch({
    control,
    name: 'feedMappings',
  }) || {};

  const theme = useTheme();
  const intl = useIntl();
  const productLabel = intl.formatMessage({ id: 'bluViuNutrition.applyFeed.mapping.product' });
  const filterOptions = createFilterOptions<FeedOption>({
    stringify: (option) => option.id + option.name,
  });
  const sortedFeedOptions = useMemo(() => [...feedOptions].sort((a, b) => a.name.localeCompare(b.name)), [feedOptions]);

  const columns = [
    {
      id: 'weight',
      label: intl.formatMessage({ id: 'bluViuNutrition.applyFeed.mapping.weight' }),
      align: 'left' as const,
    },
    {
      id: 'bluViuNutritionFeed',
      label: intl.formatMessage({ id: 'bluViuNutrition.applyFeed.mapping.feedsInBluViuNutrition' }),
      align: 'left' as const,
    },
    {
      id: 'arrow',
      label: '',
      align: 'center' as const,
    },
    {
      id: 'sapFeed',
      label: intl.formatMessage({ id: 'bluViuNutrition.applyFeed.mapping.feedsInSAP' }),
      align: 'left' as const,
    },
  ];
  const unmappedFeedIdSet = useMemo(
    () => new Set(feedProgram.mappings.filter((m) => !feedMappings[m.id]?.id).map((m) => m.id)),
    [feedProgram.mappings, feedMappings]
  );
  const selectedSapFeedIdSet = useMemo(
    () => new Set(Object.values(feedMappings).filter((m): m is FeedOption => !!m?.id).map((m) => m.id)),
    [feedMappings]
  );

  return (
    <Box>
      <List options={columns} dark sx={{ overflowX: 'hidden' }}>
        {feedProgram.mappings.map((mapping: FeedMapping, index: number) => {
          return (
            <ListRow key={`${feedProgram.feedProgramId}-${index}`}>
              <ListCell align="left" >{mapping.weight}</ListCell>
             <FormListCell align="left">
                <Select
                  label={productLabel}
                  value={mapping.bluViuNutritionFeed}
                  disabled
                  fullWidth
                  dark
                  sx={{
                    width: 340,
                  }}
                >
                  <MenuItem value={mapping.bluViuNutritionFeed}>{mapping.bluViuNutritionFeed}</MenuItem>
                </Select>
              </FormListCell>
              <ListCell align="center">
                <ArrowRight size={16} />
              </ListCell>
              <FormListCell align="left">                
                <Autocomplete
                  label={productLabel}
                  options={sortedFeedOptions}
                  getOptionLabel={(option) => `${option.name} (${option.id.replace(/^0+(?=\d)/, '')})`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionDisabled={(option) => option.id !== feedMappings[mapping.id]?.id && selectedSapFeedIdSet.has(option.id)}
                  filterOptions={filterOptions}
                  controller={{
                    name: `feedMappings.${mapping.id}`,
                    control,
                    rules: {
                      required: intl.formatMessage({ id: 'form.rules.required' }),
                      validate: (value: FeedOption | null) => !!value?.id
                    },
                  }}
                  dark
                  loading={isLoading}
                  disableCloseOnSelect={false}
                  endAdornment={unmappedFeedIdSet.has(mapping.id) ? <Info size={20} style={{ marginRight: 20 }} color={theme.palette.error.main} /> : null}
                  sx={{
                    width: 340,
                  }}
                />
              </FormListCell>
            </ListRow>
          );
        })}
      </List>
    </Box>
  );
};

export default FeedProgramMapping;
